import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiRequest, configureApiClient } from '@/shared/api/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { STORAGE_KEYS } from '@/shared/config/constants';

export type AuthUser = {
  id: string;
  email: string;
  nome: string;
  fotoUrl?: string | null;
  /**
   * Timestamp do ultimo upload local de avatar nesta sessao. Usado como
   * cache-buster em absoluteAvatarUrl(). Ausente quando user vem do
   * backend (hidratacao) -- URL renderiza sem ?v= e browser cacheia
   * normalmente.
   */
  fotoUpdatedAt?: number | null;
  onboardingCompleto: boolean;
  plano: 'FREE' | 'PREMIUM';
};

type AuthState = {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isHydrating: boolean;
};

type AuthActions = {
  login: (email: string, senha: string) => Promise<void>;
  register: (input: { email: string; senha: string; nome: string }) => Promise<void>;
  logout: () => Promise<void>;
  hydrate: () => Promise<void>;
  refreshTokens: () => Promise<string | null>;
  getMe: () => Promise<AuthUser | null>;
  setUser: (user: AuthUser) => void;
  setAccessToken: (token: string | null) => void;
};

export type AuthStore = AuthState & AuthActions;

type LoginResponse = { accessToken: string; user: AuthUser };
type MeResponse = { user: AuthUser };
type RefreshResponse = { accessToken: string };

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isHydrating: true,

      setUser: (user) => set({ user, isAuthenticated: true }),
      setAccessToken: (accessToken) => set({ accessToken, isAuthenticated: !!accessToken }),

      login: async (email, senha) => {
        const data = await apiRequest<LoginResponse>({
          method: 'POST',
          url: ENDPOINTS.AUTH.LOGIN,
          data: { email, password: senha },
        });
        set({
          user: data.user,
          accessToken: data.accessToken,
          isAuthenticated: true,
        });
      },

      register: async ({ email, senha, nome }) => {
        const data = await apiRequest<LoginResponse>({
          method: 'POST',
          url: ENDPOINTS.AUTH.REGISTER,
          data: { email, password: senha, nome },
        });
        set({
          user: data.user,
          accessToken: data.accessToken,
          isAuthenticated: true,
        });
      },

      logout: async () => {
        try {
          await apiRequest<void>({ method: 'POST', url: ENDPOINTS.AUTH.LOGOUT });
        } catch {
          // silencia: ainda assim limpa estado local
        }
        set({ user: null, accessToken: null, isAuthenticated: false });
      },

      refreshTokens: async () => {
        try {
          const data = await apiRequest<RefreshResponse>({
            method: 'POST',
            url: ENDPOINTS.AUTH.REFRESH,
          });
          set({ accessToken: data.accessToken, isAuthenticated: true });
          return data.accessToken;
        } catch {
          set({ user: null, accessToken: null, isAuthenticated: false });
          return null;
        }
      },

      getMe: async () => {
        try {
          const data = await apiRequest<MeResponse>({ method: 'GET', url: ENDPOINTS.AUTH.ME });
          set({ user: data.user, isAuthenticated: true });
          return data.user;
        } catch {
          return null;
        }
      },

      hydrate: async () => {
        set({ isHydrating: true });
        const token = await get().refreshTokens();
        if (token) {
          await get().getMe();
        }
        set({ isHydrating: false });
      },
    }),
    {
      name: STORAGE_KEYS.AUTH,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ user: state.user }),
    },
  ),
);

configureApiClient({
  getAccessToken: () => useAuthStore.getState().accessToken,
  onUnauthorized: () => {
    useAuthStore.setState({ user: null, accessToken: null, isAuthenticated: false });
  },
  onTokenRefreshed: (token) => {
    useAuthStore.setState({ accessToken: token, isAuthenticated: true });
  },
});
